import { Form, Formik } from "formik";
import { useState } from "react";
import { useMutation } from "react-query";
import { resetPassword } from "../../services/auth.service";
import * as Yup from "yup";
import { CustomFieldWithError } from "../../components/forms/CustomFieldWithError";
import { useTranslation } from "react-i18next";
import Button from "../../components/button";
import { ButtonSize, ButtonVariant } from "../../components/button/types";
import { Password } from "../../components/forms/Password";
import {
  PASSWORD_RESET_ERRORS,
  SIGNIN_ERRORS,
  SIGNUP_ERRORS,
} from "../../errors/errorCodes";

const validationSchema = Yup.object().shape({
  password: Yup.string().required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), ""], "Passwords must match")
    .required("Confirm password is required"),
});

export default function PasswordReset({
  onSuccessfulReset,
}: {
  onSuccessfulReset: () => void;
}) {
  const token = new URLSearchParams(window.location.search).get("token");

  const { t } = useTranslation("authentication");

  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetPasswordMutation = useMutation(resetPassword, {
    onSuccess: () => {
      setMessage("Password successfully reset. Redirecting to login page...");
      setErrorMessage("");
      setTimeout(() => onSuccessfulReset(), 2000);
    },
    onError: ({ response }) => {
      if (response.data.message == SIGNUP_ERRORS.PASSWORD_NOT_STRONG_ENOUGH) {
        setErrorMessage(t("register-form.password-not-strong.message"));
      } else if (
        response.data.message ==
        PASSWORD_RESET_ERRORS.INVALID_RESET_PASSWORD_VERIFICATION_TOKEN
      ) {
        setErrorMessage(t("password-reset-invalid-token"));
      }
    },
  });

  return (
    <div className="w-full p-9">
      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          resetPasswordMutation.mutate({ ...values, token });
        }}
      >
        {({ errors, touched, isValid, values }) => {
          const noFieldsEmpty =
            values["password"].length > 0 &&
            values["confirmPassword"].length > 0;
          const disabled = !isValid || !noFieldsEmpty;

          return (
            <Form>
              <div className="w-full mt-4">
                <label htmlFor="password">{t("register-form.password")}*</label>
                <div>
                  <Password
                    error={errors.password || ""}
                    touched={!!touched.password}
                    id="password"
                    name="password"
                  />
                </div>
              </div>
              <div className="w-full mt-4">
                <label htmlFor="confirmPassword">
                  {t("register-form.confirm-password")}*
                </label>
                <div>
                  <Password
                    error={errors.confirmPassword || ""}
                    touched={!!touched.confirmPassword}
                    id="confirmPassword"
                    name="confirmPassword"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="mt-6 w-full"
                disabled={disabled}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.MEDIUM}
              >
                {t("buttons.confirm-password-reset")}
              </Button>
            </Form>
          );
        }}
      </Formik>

      {message ? (
        <div className="flex justify-end text-green-900 mt-1">{message}</div>
      ) : null}
      {errorMessage ? (
        <div className="flex justify-end text-red-900 mt-1">{errorMessage}</div>
      ) : null}
    </div>
  );
}
